import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type PendencyWireItem, type PersonalPendencyMetricsRaw } from '../lib/api-client';

/**
 * useListMyPendencies — caixa pessoal do atendente (`GET /v1/pendencies`,
 * scoped ao operador pelo backend). Tela 3 (Brief 2B). `status` é server-side
 * (D2); prioridade/categoria/prazo são filtrados client-side no adapter.
 */
export function useListMyPendencies(
  params: Parameters<typeof api.listMyPendencies>[0] = {},
): ReturnType<typeof useQuery<{ data: PendencyWireItem[] }, Error>> {
  return useQuery({
    queryKey: ['pendencies', 'list', params],
    queryFn: () => api.listMyPendencies(params),
    staleTime: 30_000,
  });
}

/**
 * useMyPendencyMetrics — métricas da própria caixa (Item 346 D3). O fetch é
 * GATEADO por `enabled` (a aba Métricas só busca quando ativa — não dispara o
 * GET enquanto o atendente está na Inbox). Caller-scoped pelo backend.
 */
export function useMyPendencyMetrics(
  enabled: boolean,
): ReturnType<typeof useQuery<PersonalPendencyMetricsRaw, Error>> {
  return useQuery({
    queryKey: ['pendencies', 'my-metrics'],
    queryFn: () => api.getMyPendencyMetrics(),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * useCreatePendency — cria uma pendência (ADR-109) a partir da Conversa em Curso.
 *
 * Em sucesso invalida o prefixo `['pendencies']`, refrescando o bloco
 * "Pendências do atendente" do painel direito (`use-panel-fixtures`).
 */
export function useCreatePendency(): ReturnType<
  typeof useMutation<PendencyWireItem, Error, Parameters<typeof api.createPendency>[0]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createPendency>[0]) => api.createPendency(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pendencies'] });
    },
  });
}

/** Variáveis da resolução de pendência (ADR-109 + If-Match ADR-071). */
interface ResolvePendencyVars {
  pendencyId: string;
  expectedVersion: number;
  body?: Parameters<typeof api.resolvePendency>[2];
}

/**
 * useResolvePendency — resolve a pendência (`POST /v1/pendencies/:id/resolve`)
 * a partir do bloco "Pendências do atendente" do painel direito.
 *
 * Em sucesso invalida o prefixo `['pendencies']`, refrescando o bloco.
 */
export function useResolvePendency(): ReturnType<
  typeof useMutation<PendencyWireItem, Error, ResolvePendencyVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pendencyId, expectedVersion, body }: ResolvePendencyVars) =>
      api.resolvePendency(pendencyId, expectedVersion, body ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pendencies'] });
    },
  });
}

/** Variáveis do adiar (postpone) — If-Match obrigatório (ADR-071). */
interface PostponePendencyVars {
  pendencyId: string;
  expectedVersion: number;
  body: Parameters<typeof api.postponePendency>[2];
}

/**
 * usePostponePendency — adia a pendência (`POST /v1/pendencies/:id/postpone`).
 * `status` vira `postponed` e `postponeCount` incrementa. Invalida `['pendencies']`.
 */
export function usePostponePendency(): ReturnType<
  typeof useMutation<PendencyWireItem, Error, PostponePendencyVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pendencyId, expectedVersion, body }: PostponePendencyVars) =>
      api.postponePendency(pendencyId, expectedVersion, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pendencies'] });
    },
  });
}

/** Variáveis do retomar (resume) — If-Match obrigatório (ADR-071). */
interface ResumePendencyVars {
  pendencyId: string;
  expectedVersion: number;
  body?: Parameters<typeof api.resumePendency>[2];
}

/**
 * useResumePendency — retoma a pendência adiada (`POST /v1/pendencies/:id/resume`,
 * `postponed → open`). Invalida `['pendencies']`.
 */
export function useResumePendency(): ReturnType<
  typeof useMutation<PendencyWireItem, Error, ResumePendencyVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pendencyId, expectedVersion, body }: ResumePendencyVars) =>
      api.resumePendency(pendencyId, expectedVersion, body ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pendencies'] });
    },
  });
}

/** Variáveis do PATCH de pendência (cancelar/reassignar/editar). */
interface UpdatePendencyVars {
  pendencyId: string;
  expectedVersion: number;
  body: Parameters<typeof api.updatePendency>[2];
}

/**
 * useUpdatePendency — PATCH genérico da pendência (`PATCH /v1/pendencies/:id`)
 * usado por Cancelar (`status='cancelled'`) e refinamentos futuros.
 *
 * Em sucesso invalida o prefixo `['pendencies']`.
 */
export function useUpdatePendency(): ReturnType<
  typeof useMutation<PendencyWireItem, Error, UpdatePendencyVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pendencyId, expectedVersion, body }: UpdatePendencyVars) =>
      api.updatePendency(pendencyId, expectedVersion, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pendencies'] });
    },
  });
}
